import React, { useMemo, useId } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import { formatValue, normalizeTimestamp } from './chartUtils';
import { parseThresholds, matchThreshold } from './thresholdUtils';
import WidgetState from './WidgetState';

/**
 * Stat Widget - Single value display with sparkline and trend
 * The most essential observability widget (like Grafana's "Stat" panel)
 */
const StatWidget = ({ data, config = {} }) => {
  // `sparkGrad-${title}` was not unique: title is '' for scalar and table
  // results, so every such stat widget defined the SAME id, and titles
  // containing '.' or '/' produced ids that broke the url(#...) reference
  // outright. useId is per-instance and always reference-safe.
  // useId() embeds colons (":r0:"); strip them so the id stays a plain
  // token in every context that consumes it.
  const gradientId = `sparkGrad${useId().replace(/:/g, '')}`;
  const theme = useTheme();

  const processed = useMemo(() => {
    if (!data || !data.data) {
      return null;
    }

    let value = 0;
    let title = '';
    let sparklineData = [];

    if (data.type === 'timeseries' && Array.isArray(data.data) && data.data.length > 0) {
      const series = data.data[0];
      if (series && series.values && series.values.length > 0) {
        const lastPoint = series.values[series.values.length - 1];
        value = parseFloat(lastPoint[1]) || 0;

        if (series.metric && series.metric.__name__) {
          title = series.metric.__name__;
        }

        // Build sparkline data — normalize timestamps to milliseconds
        sparklineData = series.values.map(point => ({
          t: normalizeTimestamp(point[0]),
          v: parseFloat(point[1]) || 0,
        }));

        // Calculate trend
        const vals = series.values.map(p => parseFloat(p[1]));
        const midpoint = Math.floor(vals.length / 2);
        if (midpoint > 0) {
          const firstHalf = vals.slice(0, midpoint);
          const secondHalf = vals.slice(midpoint);
          const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
          const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
          const pctChange = avgFirst !== 0 ? ((avgSecond - avgFirst) / Math.abs(avgFirst)) * 100 : 0;

          return { value, title, sparklineData, pctChange, hasData: true };
        }
      }
    } else if (data.type === 'scalar') {
      value = parseFloat(data.data);
      // A null/non-numeric scalar is NOT a zero — show the empty state.
      if (!Number.isFinite(value)) return { hasData: false };
      return { value, title, sparklineData: [], pctChange: 0, hasData: true };
    } else if (data.type === 'table' && data.rows && data.rows.length > 0) {
      // Table result from ClickHouse SQL - extract first numeric value
      const firstRow = data.rows[0];
      const numericValue = Object.values(firstRow).find(v => typeof v === 'number');
      if (numericValue !== undefined) {
        value = numericValue;
        return { value, title, sparklineData: [], pctChange: 0, hasData: true };
      }
    }

    // Empty result set (offline host, filtered-out window, …): render the honest
    // "No data" state. This used to fall through with hasData:true and value 0,
    // which painted a fabricated (and green!) 0.0 on dashboards.
    if (sparklineData.length > 0) {
      return { value, title, sparklineData, pctChange: 0, hasData: true };
    }
    return { hasData: false };
  }, [data]);

  // Threshold coloring is driven by the widget's configured thresholds, not a
  // hardcoded 0-100 band. A 500ms latency with a "> 400" rule now goes red;
  // with no rules configured it stays a neutral (theme text) colour instead of
  // being force-fit onto a percent scale.
  const thresholds = useMemo(() => parseThresholds(config.thresholdsJson), [config.thresholdsJson]);

  if (!processed || !processed.hasData) {
    return <WidgetState isEmpty />;
  }

  const { value, title, sparklineData, pctChange } = processed;

  const thresholdMatch = matchThreshold(value, thresholds);
  const thresholdColor = thresholdMatch ? thresholdMatch.color : theme.palette.text.primary;

  // Trend direction
  const TrendIcon = pctChange > 1 ? TrendingUpIcon : pctChange < -1 ? TrendingDownIcon : TrendingFlatIcon;
  const trendColor = pctChange > 1 ? '#52c41a' : pctChange < -1 ? '#f5222d' : theme.palette.text.secondary;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Metric name */}
      {title && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 0.5,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {title}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: 'clamp(2rem, 8vw, 3.5rem)',
          fontWeight: 700,
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1.1,
          color: thresholdColor,
          textAlign: 'center',
          letterSpacing: '-0.05em'
        }}
      >
        {formatValue(value, config)}
      </Typography>

      {/* Trend indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <TrendIcon sx={{ fontSize: 18, color: trendColor }} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: trendColor,
            fontSize: '0.8rem',
          }}
        >
          {pctChange > 0 ? '+' : ''}{pctChange.toFixed(1)}%
        </Typography>
      </Box>

      {/* Sparkline */}
      {sparklineData.length > 1 && (
        <Box sx={{ width: '100%', height: 40, mt: 1, opacity: 0.7 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparklineData}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={thresholdColor} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={thresholdColor} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke={thresholdColor}
                fill={`url(#${gradientId})`}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(StatWidget, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator &&
    prevProps.config?.colorScheme === nextProps.config?.colorScheme &&
    prevProps.config?.thresholdsJson === nextProps.config?.thresholdsJson
  );
});
